import { Flex, Typography } from "../ui";
import React from "react";
import { DocumentAnnotationLayer, type DocumentAnnotation } from "./DocumentAnnotations";
import { rotateDocumentAnnotation } from "./documentAnnotationGeometry";
import { locateDocumentText, type DocumentTextAnnotation, type DocumentTextPage } from "./documentTextAnnotations";
import { DOCUMENT_VIEWER_MAX_FIT_ZOOM, PDF_CSS_UNITS, type DocumentViewerZoom } from "./documentViewerZoom";
import { loadPdfjs, type PdfDocumentProxy, type PdfLoadingTask } from "./pdfjsLoader";
import { pdfTextBox } from "./pdfTextGeometry";
import { FULL_TURN_DEGREES } from "./documentRotation";
import "./documentViewer.css";

const PDF_PREVIEW_MAX_DEVICE_PIXEL_RATIO = 2;
const PDFJS_RENDERING_CANCELLED = "RenderingCancelledException";
const FIT_EPSILON = 0.001;
/** A page counts as current once it crosses the top third of the scroll container. */
const ACTIVE_PAGE_VIEWPORT_DIVISOR = 3;
/** Papierfläche eines PDF-Blatts: unabhängig vom Theme weiß, sonst wäre dunkle Schrift auf dunklem Grund unlesbar. */
// design-rule-exception: raw-color Papierfarbe des Blatts, nicht Teil des Themes
const PDF_PAPER_COLOR = "#ffffff";

export interface PdfInlinePreviewProps {
  annotations?: readonly DocumentAnnotation[];
  textAnnotations?: readonly DocumentTextAnnotation[];
  selectedAnnotationId?: string | null;
  onAnnotationSelect?: (id: string) => void;
  fileData?: Uint8Array;
  fileUrl?: string;
  title: string;
  /** Wird anstelle der Seiten gezeigt, bis das Dokument geladen ist. */
  loadingContent: React.ReactNode;
  errorText: string;
  zoom: DocumentViewerZoom;
  /** Zusätzliche Drehung im Uhrzeigersinn in Grad (Vielfache von 90). */
  rotation?: number;
  /** Springt zur Seite; ein neuer `key` löst den Sprung auch zur selben Seite erneut aus. */
  pageRequest?: { page: number; key: number } | null;
  onPageCountChange?: (pageCount: number) => void;
  onCurrentPageChange?: (page: number) => void;
  /** Tatsächlich angezeigter Anteil (1 = 100 %), auch bei Einpassung. */
  onEffectiveZoomChange?: (zoom: number) => void;
  onReadyChange?: (ready: boolean) => void;
}

type PdfPageCanvasProps = {
  annotations?: readonly DocumentAnnotation[];
  selectedAnnotationId?: string | null;
  onAnnotationSelect?: (id: string) => void;
  pdfDocument: PdfDocumentProxy;
  pageNumber: number;
  scale: number;
  rotation: number;
  errorText: string;
};

function pageRotation(page: unknown, rotation: number) {
  return (((page as { rotate?: number }).rotate ?? 0) + rotation) % FULL_TURN_DEGREES;
}

function PdfPageCanvas({ pdfDocument, pageNumber, scale, rotation, errorText, annotations, selectedAnnotationId, onAnnotationSelect }: PdfPageCanvasProps) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [rendering, setRendering] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    let renderTask: { cancel: () => void; promise: Promise<unknown> } | null = null;

    setFailed(false);
    setRendering(true);

    const renderPage = async () => {
      try {
        const page = await pdfDocument.getPage(pageNumber);
        if (cancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const viewport = page.getViewport({ scale, rotation: pageRotation(page, rotation) });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, PDF_PREVIEW_MAX_DEVICE_PIXEL_RATIO);
        const context = canvas.getContext("2d", { alpha: false });
        if (!context) throw new Error("Canvas context unavailable.");

        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.fillStyle = PDF_PAPER_COLOR;
        context.fillRect(0, 0, viewport.width, viewport.height);

        renderTask = page.render({ canvas, canvasContext: context, viewport }) as typeof renderTask;
        await renderTask?.promise;
      } catch (error: any) {
        if (!cancelled && error?.name !== PDFJS_RENDERING_CANCELLED) setFailed(true);
      } finally {
        if (!cancelled) setRendering(false);
      }
    };

    void renderPage();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [errorText, pageNumber, pdfDocument, rotation, scale]);

  if (failed) {
    return (
      <Flex align="center" justify="center" className="mq-document-page-error" data-document-page={pageNumber}>
        <Typography.Text type="secondary">{errorText}</Typography.Text>
      </Flex>
    );
  }

  return (
    <div className="mq-document-page" data-document-page={pageNumber}>
      <canvas ref={canvasRef} className="mq-document-page-canvas" />
      {!rendering && annotations?.length ? <DocumentAnnotationLayer annotations={annotations} page={pageNumber} selectedId={selectedAnnotationId} onSelect={onAnnotationSelect} /> : null}
    </div>
  );
}

/** Innenmaße ohne Polster: Grundlage der Einpassung. */
function contentSize(element: HTMLElement) {
  const style = getComputedStyle(element);
  const horizontal = (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);
  const vertical = (Number.parseFloat(style.paddingTop) || 0) + (Number.parseFloat(style.paddingBottom) || 0);
  return { width: Math.max(0, element.clientWidth - horizontal), height: Math.max(0, element.clientHeight - vertical) };
}

/** PDF-Seiten untereinander; Leiste, Zoomwahl und Seitenwechsel gehören dem `DocumentViewer`. */
export function PdfInlinePreview({
  fileData,
  fileUrl,
  title,
  loadingContent,
  errorText,
  zoom,
  rotation = 0,
  pageRequest,
  annotations,
  textAnnotations,
  selectedAnnotationId,
  onAnnotationSelect,
  onPageCountChange,
  onCurrentPageChange,
  onEffectiveZoomChange,
  onReadyChange,
}: PdfInlinePreviewProps) {
  const [pdfDocument, setPdfDocument] = React.useState<PdfDocumentProxy | null>(null);
  const [pageCount, setPageCount] = React.useState(0);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [fit, setFit] = React.useState<{ width: number; page: number } | null>(null);
  const pagesRef = React.useRef<HTMLDivElement | null>(null);
  const [textPages, setTextPages] = React.useState<{ document: PdfDocumentProxy; pages: DocumentTextPage[] } | null>(null);
  const hasTextAnnotations = !!textAnnotations?.length;
  React.useEffect(() => {
    if (!pdfDocument || !hasTextAnnotations) return;
    let cancelled = false;
    void (async () => {
      const pages: DocumentTextPage[] = [];
      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
        const page = await pdfDocument.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 1 });
        const content = await page.getTextContent();
        await page.getOperatorList();
        await document.fonts.ready;
        if (cancelled) return;
        const context = document.createElement("canvas").getContext("2d");
        if (!context) return;
        const boxes = content.items.flatMap(item => {
          if (!("str" in item) || !item.str.trim()) return [];
          return [pdfTextBox(item, content.styles[item.fontName], viewport, context)];
        });
        pages.push({ page: pageNumber, boxes });
        if (cancelled) return;
      }
      if (!cancelled) setTextPages({ document: pdfDocument, pages });
    })().catch(() => { if (!cancelled) setTextPages(null); }); // Locations are optional; rendering remains available.
    return () => { cancelled = true; };
  }, [pdfDocument, hasTextAnnotations]);
  const textMatches = pdfDocument && textPages?.document === pdfDocument && hasTextAnnotations ? locateDocumentText(textPages.pages, textAnnotations ?? []) : [];
  const locatedIds = new Set(textMatches.map(item => item.id));
  const visibleAnnotations = [...(annotations ?? []).filter(item => !locatedIds.has(item.id)), ...textMatches]
    .map(item => rotateDocumentAnnotation(item, rotation));
  const fitScale = zoom === "fitPage" ? fit?.page : fit?.width;
  const effectiveScale = typeof zoom === "number"
    ? zoom * PDF_CSS_UNITS
    : Math.min(fitScale ?? PDF_CSS_UNITS, DOCUMENT_VIEWER_MAX_FIT_ZOOM * PDF_CSS_UNITS);

  React.useEffect(() => {
    let cancelled = false;

    setPdfDocument(null);
    setPageCount(0);
    setLoadFailed(false);
    setFit(null);

    const source = fileUrl
      ? { url: fileUrl }
      : fileData
        ? { data: new Uint8Array(fileData) }
        : null;
    if (!source) {
      setLoadFailed(true);
      return;
    }

    let loadingTask: PdfLoadingTask | null = null;
    loadPdfjs()
      .then((pdfjs) => {
        if (cancelled) return;
        loadingTask = pdfjs.getDocument(source);
        return loadingTask.promise.then((document) => {
          if (cancelled) return;
          setPdfDocument(document);
          setPageCount(document.numPages);
        });
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });

    return () => {
      cancelled = true;
      // Der LoadingTask besitzt auch nach dem Laden Dokument und Worker.
      void loadingTask?.destroy().catch((error: unknown) => {
        console.error("PDF-Ressourcen konnten nicht freigegeben werden", error);
      });
    };
  }, [fileData, fileUrl]);

  React.useEffect(() => {
    onReadyChange?.(pdfDocument != null && !loadFailed);
    return () => onReadyChange?.(false);
  }, [pdfDocument, loadFailed, onReadyChange]);

  React.useEffect(() => { onPageCountChange?.(pageCount); }, [pageCount, onPageCountChange]);
  React.useEffect(() => { if (pdfDocument) onEffectiveZoomChange?.(effectiveScale / PDF_CSS_UNITS); }, [pdfDocument, effectiveScale, onEffectiveZoomChange]);

  React.useEffect(() => {
    if (!pdfDocument) return;

    let cancelled = false;
    let frameId: number | null = null;

    const updateFit = async () => {
      const container = pagesRef.current;
      if (!container) return;

      const page = await pdfDocument.getPage(1);
      if (cancelled) return;

      const viewport = page.getViewport({ scale: 1, rotation: pageRotation(page, rotation) });
      const available = contentSize(container);
      if (available.width <= 0 || available.height <= 0 || viewport.width <= 0 || viewport.height <= 0) return;

      const width = available.width / viewport.width;
      const wholePage = Math.min(width, available.height / viewport.height);
      setFit(previous => previous && Math.abs(previous.width - width) < FIT_EPSILON && Math.abs(previous.page - wholePage) < FIT_EPSILON
        ? previous
        : { width, page: wholePage });
    };

    const scheduleUpdate = () => {
      if (frameId != null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        void updateFit().catch(() => {
          if (!cancelled) setLoadFailed(true);
        });
      });
    };

    scheduleUpdate();
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    if (pagesRef.current) resizeObserver.observe(pagesRef.current);

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
      if (frameId != null) cancelAnimationFrame(frameId);
    };
  }, [pdfDocument, pageCount, rotation]);

  // Aktuelle Seite aus der Bildlaufposition: die letzte Seite, deren Oberkante im oberen Drittel liegt.
  const reportedPage = React.useRef(0);
  React.useEffect(() => {
    const container = pagesRef.current;
    if (!container || !pdfDocument) return;
    let frameId: number | null = null;
    const update = () => {
      frameId = null;
      const pages = Array.from(container.querySelectorAll<HTMLElement>("[data-document-page]"));
      const threshold = container.scrollTop + container.clientHeight / ACTIVE_PAGE_VIEWPORT_DIVISOR;
      let current = 1;
      for (const element of pages) if (element.offsetTop <= threshold) current = Number(element.dataset.documentPage);
      if (current !== reportedPage.current) { reportedPage.current = current; onCurrentPageChange?.(current); }
    };
    const schedule = () => { if (frameId == null) frameId = requestAnimationFrame(update); };
    update();
    container.addEventListener("scroll", schedule, { passive: true });
    // Seiten ändern ihre Höhe beim Rendern und Einpassen, ohne dass gescrollt wird.
    const observer = new ResizeObserver(schedule);
    container.querySelectorAll("[data-document-page]").forEach(element => observer.observe(element));
    return () => { observer.disconnect(); container.removeEventListener("scroll", schedule); if (frameId != null) cancelAnimationFrame(frameId); };
  }, [pdfDocument, pageCount, onCurrentPageChange]);

  React.useEffect(() => {
    const container = pagesRef.current;
    if (!container || !pageRequest) return;
    const target = container.querySelector<HTMLElement>(`[data-document-page="${pageRequest.page}"]`);
    if (!target) return;
    const padding = Number.parseFloat(getComputedStyle(container).paddingTop) || 0;
    container.scrollTo({ top: Math.max(0, target.offsetTop - padding) });
  }, [pageRequest]);

  if (loadFailed) {
    return (
      <Flex align="center" justify="center" className="mq-document-message">
        <Typography.Text type="secondary">{errorText}</Typography.Text>
      </Flex>
    );
  }

  if (!pdfDocument) return <>{loadingContent}</>;

  return (
    <div
      ref={pagesRef}
      className="mq-document-pages"
      data-pdf-inline-preview="true"
      data-zoom-mode={typeof zoom === "number" ? "fixed" : "fit"}
      role="region"
      aria-label={title}
      tabIndex={0}
    >
      {Array.from({ length: pageCount }, (_, index) => (
        <PdfPageCanvas
          key={index + 1}
          errorText={errorText}
          pageNumber={index + 1}
          pdfDocument={pdfDocument}
          scale={effectiveScale}
          rotation={rotation}
          annotations={visibleAnnotations}
          selectedAnnotationId={selectedAnnotationId}
          onAnnotationSelect={onAnnotationSelect}
        />
      ))}
    </div>
  );
}
